/**
 * The trade log.
 *
 * Trades are DECLARED in Discord #trade-declarations — that stays the venue, because the 24-hour
 * counter-offer window (2013-II-1) happens in conversation. What the league has never had is a
 * ledger: the Draftboard workbook's Trade Log tab is eleven years of free text in two columns
 * per side, and when somebody asks "wait, whose ninth is that?" in August, the answer is a scroll.
 *
 * So this reads the log as a ledger. Every trade knows which season it belongs to (the season of
 * the PICKS it moves, not the calendar year it was agreed — the November deal is a 2026 trade),
 * who was on each side, and what actually crossed. Where the sheet wrote something the parser
 * cannot turn into an asset — "right to swap 6th rd picks in 2026 (John may decline)" — the
 * original text survives as a note rather than being rounded into a pick nobody owns.
 *
 * Counting is done here rather than in the view, because "who trades with whom" is the question
 * the league actually argues about and it should have one answer.
 */

import tradeData from '../data/trade-log.json';
import { faab } from './faab.js';
import type { GmId, Trade } from '../store/types.js';
import type { ParsedSide, ParsedTrade } from './tradeParse.js';

export interface TradedPick {
  season: number | null;
  round: number | null;
  slot: number | null;
  /** A farm-auction nomination slot (2015-IV-2), not a draft pick. */
  farm: boolean;
  /** Exactly what the sheet wrote. */
  raw: string;
}

export interface TradeSideRecord {
  gm: string;
  players: string[];
  /** Keeper rights to a farm player, which move separately from the player. */
  rights: string[];
  picks: TradedPick[];
  faab: number;
}

export interface TradeRecord {
  id: string;
  season: number | null;
  date: string | null;
  /** False where the sheet never wrote one down. Six 2025 rows are like this. */
  dateResolved: boolean;
  sides: [TradeSideRecord, TradeSideRecord];
  notes?: string[];
  /** Set on a trade recorded in the app rather than lifted from the sheet. */
  addedBy?: GmId;
  addedAt?: string;
  /** How an option in the notes was resolved, once it has been. See `Trade.clauseOutcome`. */
  clauseOutcome?: Trade['clauseOutcome'];
}

const DATA = tradeData as unknown as { source: string; readOn: string; trades: TradeRecord[] };

export const TRADES: TradeRecord[] = DATA.trades;
export const TRADE_SOURCE = DATA.source;

/* -- trades recorded in the app ---------------------------------------------------------------- */

/**
 * A reviewed draft, on its way to the store.
 *
 * The store's `Trade` is the storage shape and keeps picks as the strings a person wrote, because
 * the ledger's job is to record what was agreed, not to re-notate it. The parse survives only as
 * far as the review screen.
 */
/**
 * An existing trade, back in the shape the record form edits.
 *
 * The inverse of `toStoreTrade`, and it exists so that correcting a trade is the same screen as
 * recording one. The alternative — a second editor — is a second place for the two to disagree
 * about what a side may contain.
 */
export function toParsedTrade(t: Trade): ParsedTrade {
  const side = (i: 0 | 1): ParsedSide => {
    const s = t.sides[i];
    return {
      gm: s.gm ? s.gm.toUpperCase() : null,
      /* `raw` is what the person originally typed. There is none to recover from a stored trade,
         so the cleaned text stands in for it — which is honest: by this point it IS the source. */
      assets: [
        ...s.players.map((text) => ({ kind: 'player' as const, text, raw: text })),
        ...s.rights.map((text) => ({ kind: 'rights' as const, text, raw: text })),
        ...s.picks.map((text) => ({ kind: 'pick' as const, text, raw: text })),
        ...(s.faab
          ? [{ kind: 'faab' as const, text: `₣${s.faab} FAAB`, raw: `${s.faab} FAAB`, amount: s.faab }]
          : []),
      ],
    };
  };
  return {
    sides: [side(0), side(1)],
    date: t.date || null,
    season: t.season,
    warnings: [],
    /* The note came out of leftovers on the way in, so it goes back the same way and survives a
       round trip through the form rather than being dropped by it. */
    leftovers: t.note ? t.note.split(' · ') : [],
  };
}

/**
 * `id` is passed when an existing trade is being corrected. Without it a new one is minted, and
 * an edit that minted a new id would leave the original standing beside its own correction.
 */
export function toStoreTrade(draft: ParsedTrade, actor: GmId, id?: string): Trade {
  const side = (i: 0 | 1) => {
    const s = draft.sides[i];
    return {
      gm: (s.gm ?? '').toUpperCase(),
      players: s.assets.filter((a) => a.kind === 'player').map((a) => a.text),
      rights: s.assets.filter((a) => a.kind === 'rights').map((a) => a.text),
      picks: s.assets.filter((a) => a.kind === 'pick').map((a) => a.text),
      faab: s.assets.filter((a) => a.kind === 'faab').reduce((n, a) => n + (a.amount ?? 0), 0),
    };
  };
  const now = new Date().toISOString();
  return {
    // Date-stamped rather than sequential: the bundled ids are t001-t109 and these must not collide.
    id: id ?? `app-${now.replace(/[^0-9]/g, '').slice(0, 17)}`,
    season: draft.season,
    date: draft.date ?? now.slice(0, 10),
    sides: [side(0), side(1)],
    recordedBy: actor,
    recordedAt: now,
    ...(draft.leftovers.length ? { note: draft.leftovers.join(' · ') } : {}),
  };
}

/** The store's shape, read back as a log row. */
export function fromStoreTrade(t: Trade): TradeRecord {
  const side = (i: 0 | 1): TradeSideRecord => {
    const s = t.sides[i];
    return {
      gm: s.gm.toUpperCase(),
      players: [...s.players],
      rights: [...s.rights],
      picks: s.picks.map((raw) => {
        const year = raw.match(/\b(20\d\d)\b/);
        const dotted = raw.match(/\b(\d{1,2})\.(x|\d{1,2})\b/i);
        const ord = raw.match(/\b(\d{1,2})\s*(?:st|nd|rd|th)\b/i);
        const round = dotted ? Number(dotted[1]) : ord ? Number(ord[1]) : null;
        const slot = dotted && dotted[2]!.toLowerCase() !== 'x' ? Number(dotted[2]) : null;
        return {
          season: year ? Number(year[1]) : null,
          round,
          slot,
          farm: /farm/i.test(raw),
          raw,
        };
      }),
      faab: s.faab,
    };
  };
  return {
    id: t.id,
    season: t.season,
    date: t.date,
    dateResolved: Boolean(t.date),
    sides: [side(0), side(1)],
    ...(t.note ? { notes: [t.note] } : {}),
    ...(t.clauseOutcome ? { clauseOutcome: t.clauseOutcome } : {}),
    addedBy: t.recordedBy,
    addedAt: t.recordedAt,
  };
}

/** The bundled log plus anything recorded in the app, newest first. */
/**
 * The bundled log with the database laid over it.
 *
 * THREE THINGS A STORED ROW CAN BE, and they are told apart by id alone:
 *
 *   a NEW trade — an id nothing in the file uses, so it is simply added;
 *   a CORRECTION — the same id as a bundled row, which replaces it. Eleven years of this log came
 *     out of a spreadsheet somebody typed by hand, and a commissioner who finds a wrong one has
 *     to be able to fix it without the file being rebuilt;
 *   a STRIKE — `voided`, which removes the row whether it was bundled or stored. See the comment
 *     on `Trade.voided` for why a delete cannot do this job.
 *
 * Later wins on purpose: the database is the correction and the file is the original.
 */
export function allTrades(added: readonly Trade[] = []): TradeRecord[] {
  const by = new Map<string, TradeRecord>(TRADES.map((t) => [t.id, t]));
  for (const t of added) {
    if (t.voided) by.delete(t.id);
    else by.set(t.id, fromStoreTrade(t));
  }
  return [...by.values()]
    .sort((x, y) => (y.date ?? '').localeCompare(x.date ?? '') || (y.season ?? 0) - (x.season ?? 0));
}

/** Newest first, and undated rows sink to the bottom of their season rather than the top. */
export function tradeSeasons(list: readonly TradeRecord[] = TRADES): number[] {
  return [...new Set(list.map((t) => t.season).filter((s): s is number => s !== null))]
    .sort((a, b) => b - a);
}

export interface TradeFilter {
  season?: number | 'all';
  gm?: string | 'all';
  /** Matches a player, a rights line, or a pick as written. */
  q?: string;
}

export function searchTrades(f: TradeFilter, list: readonly TradeRecord[] = TRADES): TradeRecord[] {
  const q = (f.q ?? '').trim().toLowerCase();
  return list.filter((t) => {
    if (f.season && f.season !== 'all' && t.season !== f.season) return false;
    if (f.gm && f.gm !== 'all' && !t.sides.some((s) => s.gm === f.gm)) return false;
    if (!q) return true;
    return t.sides.some((s) => [
      s.gm,
      ...s.players,
      ...s.rights,
      ...s.picks.map((p) => p.raw),
    ].some((x) => x.toLowerCase().includes(q)));
  });
}

/** One line describing what a side got, in the order a person reads it. */
export function describeSide(s: TradeSideRecord): string[] {
  return [
    ...s.players,
    ...s.rights.map((r) => `rights to ${r}`),
    ...s.picks.map((p) => p.raw),
    ...(s.faab ? [`${faab(s.faab)} FAAB`] : []),
  ];
}

export interface TradePartners {
  gms: string[];
  /** How many trades each pair has made. Symmetric. */
  grid: Map<string, Map<string, number>>;
  totals: Map<string, number>;
}

/** Who deals with whom. The pair counts are the part the league finds funny and/or damning. */
export function tradePartners(trades: TradeRecord[] = TRADES): TradePartners {
  const grid = new Map<string, Map<string, number>>();
  const totals = new Map<string, number>();
  const seen = new Set<string>();

  for (const t of trades) {
    const [a, b] = t.sides;
    if (a.gm === b.gm) continue;
    seen.add(a.gm);
    seen.add(b.gm);
    for (const [x, y] of [[a.gm, b.gm], [b.gm, a.gm]] as const) {
      const row = grid.get(x) ?? new Map<string, number>();
      row.set(y, (row.get(y) ?? 0) + 1);
      grid.set(x, row);
      totals.set(x, (totals.get(x) ?? 0) + 1);
    }
  }

  return { gms: [...seen].sort(), grid, totals };
}

/** Assets a GM has moved, for the "what did he actually give up" argument. */
export function tradeVolume(gm: string): {
  trades: number; playersIn: number; playersOut: number; picksIn: number; picksOut: number;
  faabIn: number; faabOut: number;
} {
  let out = {
    trades: 0, playersIn: 0, playersOut: 0, picksIn: 0, picksOut: 0, faabIn: 0, faabOut: 0,
  };
  for (const t of TRADES) {
    const mine = t.sides.find((s) => s.gm === gm);
    const theirs = t.sides.find((s) => s.gm !== gm);
    if (!mine || !theirs) continue;
    out = {
      trades: out.trades + 1,
      playersIn: out.playersIn + mine.players.length + mine.rights.length,
      playersOut: out.playersOut + theirs.players.length + theirs.rights.length,
      picksIn: out.picksIn + mine.picks.length,
      picksOut: out.picksOut + theirs.picks.length,
      faabIn: out.faabIn + mine.faab,
      faabOut: out.faabOut + theirs.faab,
    };
  }
  return out;
}

/* -- what still changes the upcoming draft ----------------------------------------------------- */

/**
 * `In the upcoming draft` — the one filter the league actually wants, and the one the season
 * chips cannot express.
 *
 * A trade files under the season of the PICKS it moves, so most deals that bear on 2026 are already
 * filed as 2026. But not all of them: a 2024 trade that sent a 2026 second is filed under 2024 by
 * the sheet's own convention where the writer dated it by the picks he thought it moved, and an
 * unresolved clause — "right to swap 6th rd picks in 2026 (John may decline)" — changes the board
 * without moving anything at all until somebody decides.
 *
 * So the filter is a question about CONTENT, not about the filing year, and it is why there is no
 * separate `2026` chip: it would sit next to this one showing a subset of it and nobody would
 * know which to press.
 */
export function changesDraft(t: TradeRecord, season: number): boolean {
  if (t.season === season) return true;
  const year = String(season);
  const inPicks = t.sides.some((s) => s.picks.some(
    (p) => p.season === season || (p.season === null && p.raw.includes(year)),
  ));
  if (inPicks) return true;
  return (t.notes ?? []).some((n) => n.includes(year));
}

/** How many of the archive still bear on this year's board. */
export function draftRelevant(list: readonly TradeRecord[], season: number): TradeRecord[] {
  return list.filter((t) => changesDraft(t, season));
}
